import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { initializeSession } from '../../lib/auth'
import { type Lang } from '../../lib/i18n'
import { changeOwnPassword, StaffApiError } from '../../lib/staff-api'
import { st } from './staff-i18n'
import { PasswordInput } from '../../components/PasswordInput'
import { CommandHeader } from '../../components/CommandHeader'

interface Props {
  lang: Lang
  onLangToggle: () => void
  isOnline: boolean
}

type AuthState = 'checking' | 'ok' | 'redirect'

// bench-STAFF-LOGIN-OFF-AND-OWN-PASSWORD.CB-B §1.8. Any signed-in HOP user changes their own
// password with the current one — no role gate, unlike StaffScreen (canManageStaff()). A
// person without a session is sent to /login, same as the other screens.
export function AccountScreen({ lang, onLangToggle, isOnline }: Props) {
  const [authState, setAuthState] = useState<AuthState>('checking')
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    initializeSession().then((session) => {
      setAuthState(session.authenticated ? 'ok' : 'redirect')
    })
  }, [])

  if (authState === 'checking') return null
  if (authState === 'redirect') return <Navigate to="/login" replace />

  const dir = lang === 'ar' ? 'rtl' : 'ltr'

  const canSubmit =
    next.length >= 8 && next === again && next !== current && !submitting

  function failureMessageFor(err: unknown): string {
    if (err instanceof StaffApiError) {
      if (err.status === 403) return st(lang, 'accountError403')
      if (err.status === 429) return st(lang, 'accountError429')
      return st(lang, 'accountErrorOther')
    }
    // A thrown non-StaffApiError here is a network failure — the offline case, caught
    // mid-submit rather than pre-empted by the disabled action below.
    return st(lang, 'accountErrorOffline')
  }

  async function submit() {
    setSubmitting(true)
    setError(null)
    setSuccess(false)
    try {
      await changeOwnPassword(current, next)
      // Success: a line saying the password changed, all inputs clear, THEN the screen
      // dispatches hop:auth-required — every session ends, this one included, and the
      // existing overlay (App.tsx) asks the person to sign in again with the new password.
      setCurrent('')
      setNext('')
      setAgain('')
      setSubmitting(false)
      setSuccess(true)
      window.dispatchEvent(new CustomEvent('hop:auth-required'))
    } catch (err) {
      // Failure: the inputs stay — only the pending flag clears and an error message
      // appears, and NO hop:auth-required (unlike success, this did not end the session).
      setSubmitting(false)
      setError(failureMessageFor(err))
    }
  }

  return (
    <div dir={dir} className="min-h-screen bg-background font-sans">
      <div className="sticky top-0 z-10 shadow">
        <CommandHeader title={st(lang, 'accountTitle')} lang={lang} onLangToggle={onLangToggle} isOnline={isOnline} />
      </div>

      <main className="px-4 py-6 max-w-sm mx-auto pb-10">
        <form
          className="space-y-3"
          data-testid="account-form"
          onSubmit={(e) => {
            e.preventDefault()
            if (canSubmit) void submit()
          }}
        >
          {success && (
            <div
              className="rounded-md bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-700"
              data-testid="account-success"
            >
              {st(lang, 'accountSuccess')}
            </div>
          )}

          {error && (
            <div
              className="rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2 text-sm text-destructive"
              data-testid="account-error"
            >
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {st(lang, 'accountFieldCurrent')}
            </label>
            <PasswordInput
              lang={lang}
              autoComplete="current-password"
              data-testid="account-current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              disabled={!isOnline}
              className="w-full rounded-md border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {st(lang, 'accountFieldNew')}
            </label>
            <PasswordInput
              lang={lang}
              autoComplete="new-password"
              data-testid="account-new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              disabled={!isOnline}
              className="w-full rounded-md border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {st(lang, 'accountFieldConfirm')}
            </label>
            <PasswordInput
              lang={lang}
              autoComplete="new-password"
              data-testid="account-confirm-password"
              value={again}
              onChange={(e) => setAgain(e.target.value)}
              disabled={!isOnline}
              className="w-full rounded-md border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {!isOnline && (
            <p className="text-xs text-amber-600" data-testid="account-offline">
              {st(lang, 'accountErrorOffline')}
            </p>
          )}

          <button
            type="submit"
            disabled={!canSubmit || !isOnline}
            data-testid="account-submit"
            className="w-full rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors min-h-[44px]"
          >
            {submitting ? st(lang, 'accountSubmitting') : st(lang, 'accountSubmit')}
          </button>
        </form>
      </main>
    </div>
  )
}
